import type { Money, MoneyServer } from '../types'

import { formatMoney } from './money.formatter'
import { formatNumber } from './number.formatter'

export type MetricFormat = 'number' | 'percentage' | 'currency'
export type MetricValue = number | Money | MoneyServer | null | undefined
export type FormatMetricOptions = {
  emptyValue?: string
  maximumFractionDigits?: number
  formatMoney?: typeof formatMoney
}

const DEFAULT_EMPTY_VALUE = '—'

/**
 * Normalise a metric value (a plain number or a Money/MoneyServer object) into a
 * number, returning null when the value is absent or not a finite number.
 * Shared so money normalisation lives in one place.
 */
export const metricValueToNumber = (value: MetricValue): number | null => {
  if (value === null || value === undefined) {
    return null
  }
  const amount = typeof value === 'number' ? value : Number(value.amount)
  return Number.isNaN(amount) ? null : amount
}

const formatMetricValue = (
  value: MetricValue,
  type: MetricFormat,
  notation: 'standard' | 'compact',
  options: FormatMetricOptions = {},
): string => {
  const amount = metricValueToNumber(value)
  if (amount === null) {
    return options.emptyValue ?? DEFAULT_EMPTY_VALUE
  }
  const maximumFractionDigits = options.maximumFractionDigits ?? (notation === 'compact' ? 1 : 2)
  if (type === 'currency' && typeof value !== 'number') {
    return (options.formatMoney ?? formatMoney)(value, {
      notation,
      maximumFractionDigits,
      allowZero: true,
    })
  }
  if (type === 'percentage') {
    return `${formatNumber(amount * 100, notation, 'decimal', maximumFractionDigits)}%`
  }
  return formatNumber(amount, notation, 'decimal', maximumFractionDigits)
}

export const formatShortValue = (
  value: MetricValue,
  type: MetricFormat,
  options?: FormatMetricOptions,
): string => formatMetricValue(value, type, 'compact', options)
export const formatLongValue = (
  value: MetricValue,
  type: MetricFormat,
  options?: FormatMetricOptions,
): string => formatMetricValue(value, type, 'standard', options)
